import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Camera, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  Download,
  RefreshCw,
  BookOpen,
  SkipForward,
  ArrowLeft,
  Loader2,
  FileText,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Papa from 'papaparse';
import type { ImportJob, ImportRow, CSVPreview } from '@/lib/types';
import { FILE_LIMITS, STORAGE_BUCKETS } from '@/lib/constants';
import { OnboardingErrorCard } from '@/components/OnboardingErrorCard';
import { markImportSkipped } from '@/lib/onboarding';

export default function OnboardingImport() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CSVPreview | null>(null);
  const [currentJob, setCurrentJob] = useState<ImportJob | null>(null);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{title: string, message: string, code?: string, tip?: string} | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get('debug') === '1' && import.meta.env.DEV;

  // Auth guard
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  const validateFile = (file: File): { valid: boolean; error?: string; code?: string } => {
    if (!file.name.toLowerCase().endsWith('.csv') && !file.type.includes('csv')) {
      return { 
        valid: false, 
        error: "Please upload a .csv file.", 
        code: 'INVALID_FILE_TYPE' 
      };
    }

    if (file.size > FILE_LIMITS.CSV_MAX_SIZE) {
      return { 
        valid: false, 
        error: `File size must be less than ${Math.round(FILE_LIMITS.CSV_MAX_SIZE / 1024 / 1024)}MB.`, 
        code: 'FILE_TOO_LARGE' 
      };
    }

    return { valid: true };
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setError({
        title: "Invalid File",
        message: validation.error!,
        code: validation.code,
        tip: "Make sure you're uploading a valid CSV file from Goodreads."
      });
      return;
    }

    setError(null);
    setFile(selectedFile);
    previewCSV(selectedFile);
  };

  const handleFilePicker = () => {
    fileInputRef.current?.click();
  };

  const previewCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      preview: 50,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const rows = results.data;
        
        if (headers.length === 0) {
          setError({
            title: "Invalid CSV",
            message: "The CSV file appears to be empty or has no headers.",
            code: 'EMPTY_CSV',
            tip: "Make sure you downloaded the correct file from Goodreads."
          });
          return;
        }

        setPreview({
          headers,
          rows: rows.slice(0, 10),
          totalRows: Math.floor(file.size / (file.size / results.data.length)),
        });
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        setError({
          title: "CSV Parse Error",
          message: "Could not read the CSV file format.",
          code: 'CSV_PARSE_ERROR',
          tip: "Try re-downloading your Goodreads export or check the file isn't corrupted."
        });
      }
    });
  };

  const handleStartImport = async () => {
    if (!file || !user) return;

    try {
      setUploading(true);
      setError(null);
      
      // Upload file to Supabase Storage
      const fileName = `${user.id}/${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKETS.IMPORTS)
        .upload(fileName, file, {
          contentType: 'text/csv'
        });

      if (uploadError) {
        throw {
          message: uploadError.message,
          code: 'STORAGE_ERROR'
        };
      }

      // Create import job
      const { data: job, error: jobError } = await supabase
        .from('import_jobs')
        .insert({
          user_id: user.id,
          source: 'goodreads',
          file_path: uploadData.path,
          status: 'pending',
          row_count: preview?.totalRows || 0,
        })
        .select()
        .single();

      if (jobError) {
        throw {
          message: jobError.message,
          code: 'DATABASE_ERROR'
        };
      }

      setCurrentJob(job as ImportJob);

      // Trigger processing
      const { error: processError } = await supabase.functions.invoke('process-goodreads-import', {
        body: { importJobId: job.id }
      });

      if (processError) {
        throw {
          message: processError.message,
          code: 'PROCESSING_ERROR'
        };
      }

      setFile(null);
      setPreview(null);

      toast({
        title: "Import started",
        description: "Your file is being processed.",
      });

    } catch (error: any) {
      console.error('Import error:', error);
      setError({
        title: "Import Failed",
        message: error.message || "Could not start the import process.",
        code: error?.code || 'UNKNOWN_ERROR',
        tip: "Please try again. If the problem persists, check your file format."
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setSkipping(true);
      
      // Mark import as skipped using orchestrator function
      await markImportSkipped(user.id);
      
      toast({
        title: "Import skipped", 
        description: "You can always import your data later from Settings."
      });
      
      navigate('/onboarding/genres', { replace: true });
    } catch (error) {
      console.error('Error skipping import:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not skip import. Please try again."
      });
    } finally {
      setSkipping(false);
    }
  };

  const handleContinue = () => {
    navigate('/onboarding/genres', { replace: true });
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/onboarding')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl mt-8">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary-foreground" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl">Import your reading data</CardTitle>
              <p className="text-muted-foreground">
                Upload your Goodreads CSV to add your books and ratings. This step is optional.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 p-3 rounded-lg border bg-muted/50 max-w-md mx-auto">
              <TrendingUp className="w-5 h-5 text-secondary flex-shrink-0" />
              <p className="text-sm font-medium">Watch your reading habit grow</p>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <OnboardingErrorCard
                title={error.title}
                message={error.message}
                code={error.code}
                tip={error.tip}
                onRetry={() => {
                  setError(null);
                  if (file) previewCSV(file);
                }}
                onBack={() => navigate('/onboarding')}
                showDebugInfo={showDebug}
                debugInfo={{
                  fileName: file?.name,
                  fileSize: file?.size,
                  fileType: file?.type,
                  lastError: error
                }}
              />
            )}

            {!error && (
              <>
                {/* File Upload */}
                {!file && !currentJob && (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                      <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">Upload CSV File</h3>
                      <p className="text-muted-foreground mb-4">
                        Select your Goodreads export file (max 20MB)
                      </p>
                      
                      {/* Hidden file input */}
                      <input
                        id="csv-input"
                        type="file"
                        accept=".csv,text/csv"
                        className="sr-only"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                      />
                      
                      {/* Primary trigger using label */}
                      <div className="flex gap-3 justify-center">
                        <label
                          htmlFor="csv-input"
                          role="button"
                          tabIndex={0}
                          className="inline-flex items-center rounded-lg px-4 py-2 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-butter)] cursor-pointer transition-colors"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              fileInputRef.current?.click();
                            }
                          }}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Choose CSV
                        </label>
                        
                        {/* Secondary trigger button */}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleFilePicker}
                        >
                          Browse Files...
                        </Button>
                      </div>
                    </div>

                    <Alert>
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>
                        <strong>How to get your CSV:</strong> Go to Goodreads.com → My Books → Import and Export → Export Library → Download CSV
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {/* File Preview */}
                {file && preview && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-medium">File Preview</h3>
                          <p className="text-sm text-muted-foreground">
                            {file.name} • {preview.totalRows} rows detected
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={clearFile}>
                          Change File
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              {preview.headers.slice(0, 5).map((header) => (
                                <th key={header} className="text-left py-2 px-3 font-medium">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {preview.rows.slice(0, 3).map((row, i) => (
                              <tr key={i} className="border-b">
                                {preview.headers.slice(0, 5).map((header, j) => (
                                  <td key={j} className="py-2 px-3 truncate max-w-[150px]">
                                    {row[header] || '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Start Import Button */}
                {file && preview && !currentJob && (
                  <Button
                    onClick={handleStartImport}
                    disabled={uploading}
                    className="w-full h-12"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Starting Import...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Start Import
                      </>
                    )}
                  </Button>
                )}

                {/* Import Status */}
                {currentJob && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-medium">Import Status</span>
                        <Badge variant={currentJob.status === 'complete' ? 'default' : 'secondary'}>
                          {currentJob.status}
                        </Badge>
                      </div>
                      <Progress value={50} className="mb-4" />
                      <p className="text-sm text-muted-foreground">
                        Processing your reading data...
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Footer Actions */}
            <div className="flex gap-4 justify-center pt-4 border-t">
              <Button
                onClick={handleSkip}
                variant="outline"
                disabled={uploading || skipping}
                className="min-w-[120px]"
              >
                {skipping ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Skipping...
                  </>
                ) : (
                  <>
                    <SkipForward className="w-4 h-4 mr-2" />
                    Skip for Now
                  </>
                )}
              </Button>
              
              {currentJob?.status === 'complete' && (
                <Button onClick={handleContinue}>
                  Continue to Genres
                </Button>
              )}
            </div>

            {/* Dev Diagnostics */}
            {showDebug && (
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <h4 className="font-medium mb-2">Debug Info</h4>
                  <pre className="text-xs">
                    {JSON.stringify({
                      pickerLinked: fileInputRef.current?.id === 'csv-input',
                      hasFile: !!file,
                      fileName: file?.name,
                      fileSize: file?.size,
                      previewRows: preview?.totalRows,
                      uploading,
                      skipping,
                      currentJob: currentJob?.status,
                      lastError: error
                    }, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}